import { useState } from 'react';

import { mergeClasses } from '@/utils';

import { StarIcon } from '@/assets/icons';

import { STAR_VARIANTS } from './Rating.constants';

interface IRating extends React.ComponentProps<'div'> {
  rating: number;
  size: 'sm' | 'md' | 'lg';
  maxRating?: number;
  editable?: boolean;
  onRatingChange?: (rating: number) => void;
}

const Rating = ({
  rating,
  maxRating = 5,
  size,
  className,
  editable = false,
  onRatingChange,
  ...props
}: IRating) => {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const displayRating = editable && hoveredRating !== null ? hoveredRating : rating;

  const handleStarClick = (starRating: number) => {
    if (editable && onRatingChange) {
      onRatingChange(starRating);
    }
  };

  const handleStarMouseEnter = (starRating: number) => {
    if (editable) {
      setHoveredRating(starRating);
    }
  };

  const handleStarMouseLeave = () => {
    if (editable) {
      setHoveredRating(null);
    }
  };

  const renderStars = () => {
    const stars: React.ReactElement[] = [];

    for (let i = 1; i <= maxRating; i++) {
      const filled = displayRating >= i;
      const partiallyFilled = displayRating > i - 1 && displayRating < i;
      const fillPercentage = partiallyFilled ? (displayRating - (i - 1)) * 100 : 0;

      stars.push(
        <button
          key={i}
          type="button"
          aria-label={`Rate ${i} star${i > 1 ? 's' : ''}`}
          disabled={!editable}
          className={mergeClasses(
            'relative bg-transparent p-0 border-none',
            editable && 'cursor-pointer',
            !editable && 'cursor-default',
          )}
          onClick={() => handleStarClick(i)}
          onMouseEnter={() => handleStarMouseEnter(i)}
          onMouseLeave={handleStarMouseLeave}
        >
          {/* Background star (empty) */}
          <StarIcon
            data-slot="rating-star-empty"
            className={mergeClasses(STAR_VARIANTS({ size }), 'text-neutral-5 dark:text-neutral-9')}
          />

          {/* Filled star */}
          <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{
              width: filled ? '100%' : `${fillPercentage}%`,
            }}
          >
            <StarIcon
              data-slot="rating-star-filled"
              className={mergeClasses(
                STAR_VARIANTS({ size }),
                'fill-primary-base text-primary-base',
              )}
            />
          </div>
        </button>,
      );
    }

    return stars;
  };

  return (
    <div data-slot="rating" className={mergeClasses('flex items-center', className)} {...props}>
      <div className="flex items-center">{renderStars()}</div>
    </div>
  );
};

export { Rating };
